import { useState, useEffect } from "react";
import { format } from "date-fns";

import {
  Grid,
  Typography,
  LoadingButton,
  DataGrid,
  Chip,
} from "@pankod/refine-mui";

import AutoGeneratedInputCmp from "components/auto-generated-input/AutoGeneratedInputCmp";
import moment from "moment";

import { useParams } from "@pankod/refine-react-router-v6";
import axios from "utils/axios";
import useLocales from "locales/useLocales";
import { toast } from "react-toastify";
import ConfirmationModal from "./ConfirmationModal";

export default function CompFields({
  register,
  control,
  errors,
  defaultLanguageRecord,
  getValues,
  setError,
  setValue,
  watch,
  disabled,
}) {
  //console.log(getValues());
  register("booking_items");
  register("booking_payments");
  register("user");
  register("bookingDate");
  register("facility");
  register("checkinDate");
  register("checkoutDate");
  register("coupon");
  register("price");

  //console.log(getValues("booking_payments"));
  //console.log(getValues("booking_items"));

  const { id } = useParams();
  const { translate } = useLocales();

  const [canRequestPayment, setCanRequestPayment] = useState(false);
  const [requestingPayment, setRequestingPayment] = useState(false);
  const [rowsSistemazioni, setRowsSistemazioni] = useState([]);
  const [rowsExtras, setRowsExtras] = useState([]);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState();
  const [totalPenalty, setTotalPenalty] = useState();

  const handleOpen = () => setOpenConfirmation(true);
  const handleClose = () => setOpenConfirmation(false);

  ///////////////////////////////////////////////////
  ///// Aziona il pagamento totale al checkout //////
  ///////////////////////////////////////////////////
  async function fullPaymentRequest() {
    setRequestingPayment(true);
    try {
      let { data } = await axios.put("/api/request-payment-booking/" + id, {
        type: "full",
      });
      if (data == "ok") {
        toast.success("Pagamento richiesto con successo");
        setCanRequestPayment(false);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      toast.error(translate(error.error.message));
    }
    setRequestingPayment(false);
  }

  /////////////////////////////////////////////////////////////
  ///// Attiva l'incasso delle penalità in caso di no-show ////
  /////////////////////////////////////////////////////////////
  async function noShowRequest() {
    setRequestingPayment(true);
    try {
      let { data } = await axios.put("/api/request-payment-booking/" + id, {
        type: "no-show",
      });
      if (data == "ok") {
        toast.success("Pagamento richiesto con successo");
        setCanRequestPayment(false);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      toast.error(translate(error.error.message));
    }
    setRequestingPayment(false);
  }

  useEffect(() => {
    let canRequest = false;
    let from = moment(getValues("checkinDate")).startOf("day");
    let to = moment(getValues("checkoutDate")).startOf("day");
    let now = moment();

    // can only request payment if we are between the chegkin date and 1 day after the checkout date
    if (now.isBetween(from, to.add(1, "day"))) {
      canRequest = true;
    } else {
      canRequest = false;
    }

    if (canRequest)
      canRequest = getValues("booking_payments")?.some(
        (item) => item.status == "pending"
      );
    setCanRequestPayment(canRequest);
  }, [getValues("booking_payments")]);

  const fields = [
    {
      type: "title",
      variant: "h6",
      label: "Informazioni generali",
    },
    {
      label: "Nome",
      defaultValue: getValues("user.firstName"),
      type: "fixed_text",
      md: 4,
    },
    {
      label: "Cognome",
      defaultValue: getValues("user.lastName"),
      type: "fixed_text",
      md: 4,
    },
    {
      label: "Struttura",
      defaultValue: getValues("facility.businessName"),
      type: "fixed_text",
      md: 4,
    },
    {
      label: "Data di prenotazione",
      defaultValue: getValues("bookingDate")
        ? format(new Date(getValues("bookingDate")), "dd/MM/yyyy")
        : "",
      type: "fixed_text",
      md: 4,
    },
    {
      label: "Arrivo",
      defaultValue: getValues("checkinDate")
        ? format(new Date(getValues("checkinDate")), "dd/MM/yyyy")
        : "",
      type: "fixed_text",
      md: 4,
    },
    {
      label: "Partenza",
      defaultValue: getValues("checkoutDate")
        ? format(new Date(getValues("checkoutDate")), "dd/MM/yyyy")
        : "",
      type: "fixed_text",
      md: 4,
    },
    {
      label: "Prezzo totale",
      defaultValue: `${getValues("price")} €`,
      type: "fixed_text",
      md: 4,
    },
    {
      label: "Stato della prenotazione",
      type: "fixed_text",
      defaultValue: getValues("booking_payments")?.every(
        (x) => x.status == "cancelled"
      )
        ? "Cancellato"
        : getValues("booking_payments")?.some((x) => x.status == "cancelled")
        ? "Parzialmente cancellato"
        : getValues("booking_payments")?.every((x) => x.status == "completed")
        ? "Prenotato"
        : getValues("booking_payments")?.some((x) => x.status == "no_show")
        ? "No show"
        : "In attesa",
      md: 4,
    },
    {
      type: "divider",
    },
    {
      type: "title",
      variant: "h6",
      label: "Coupon",
    },
    {
      label: "Nome evento",
      defaultValue: getValues("coupon.name"),
      type: "fixed_text",
      md: 4,
    },
    {
      label: "Codice evento",
      defaultValue: getValues("coupon.code"),
      type: "fixed_text",
      md: 4,
    },
    {
      type: "divider",
    },
    {
      type: "title",
      variant: "h6",
      label: "Dati prenotazione",
    },
  ];

  const columnsSistemazioni = [
    {
      field: "accommodation",
      headerName: "Sistemazioni",
      minWidth: 180,
      flex: 1,
    },
    { field: "quantity", headerName: "Quantità", minWidth: 90, flex: 1 },
    {
      field: "ratePlan",
      headerName: "Piano tariffario",
      minWidth: 180,
      flex: 1,
    },
    { field: "totGuests", headerName: "N. persone", minWidth: 60, flex: 1 },
    { field: "children", headerName: "N. bambini", minWidth: 60, flex: 1 },
    { field: "treatment", headerName: "Trattamento", minWidth: 60, flex: 1 },
    { field: "price", headerName: "Prezzo", minWidth: 60, flex: 1 },
    {
      field: "penalty",
      headerName: "Penalità No-show",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "status",
      headerName: "Stato",
      minWidth: 150,
      flex: 1,
      renderCell: function render(params) {
        let status = params.row.status;
        //console.log(params.row);
        return (
          <Grid container justifyContent="center">
            <Chip
              label={
                status === "cancelled"
                  ? "Cancellato"
                  : status === "completed"
                  ? "Pagato"
                  : status === "booked"
                  ? "Confermato"
                  : status === "no_show"
                  ? "No show"
                  : "In attesa"
              }
              color={
                status === "cancelled"
                  ? "warning"
                  : status === "booked"
                  ? "success"
                  : status === "completed"
                  ? "customBlue"
                  : status === "no_show"
                  ? "error"
                  : "info"
              }
              sx={{ minWidth: 120 }}
            />
          </Grid>
        );
      },
    },
  ];

  const columnsExtras = [
    {
      field: "extra",
      headerName: "Servizi extra",
      minWidth: 200,
      flex: 1,
    },
    { field: "quantity", headerName: "Quantità", minWidth: 90, flex: 1 },
    {
      field: "price_type",
      headerName: "Tipologia",
      minWidth: 150,
      flex: 1,
    },
    { field: "price", headerName: "Prezzo", minWidth: 90, flex: 1 },
    {
      field: "status",
      headerName: "Stato",
      minWidth: 150,
      flex: 1,
      renderCell: function render(params) {
        let status = params.row.status;
        return (
          <Grid container justifyContent="center">
            <Chip
              label={
                status === "cancelled"
                  ? "Cancellato"
                  : status === "completed"
                  ? "Pagato"
                  : status === "booked"
                  ? "Confermato"
                  : status === "no_show"
                  ? "No show"
                  : "In attesa"
              }
              color={
                status === "cancelled"
                  ? "warning"
                  : status === "booked"
                  ? "success"
                  : status === "completed"
                  ? "customBlue"
                  : status === "no_show"
                  ? "error"
                  : "info"
              }
              sx={{ minWidth: 120 }}
            />
          </Grid>
        );
      },
    },
  ];

  //fetching dati per tabelle "dati_prenotazione"
  useEffect(() => {
    //sistemazioni
    let camere = getValues("booking_payments")?.filter(
      (item) => item.connected_resource.type == "booking-item"
    );
    //console.log("Camere", camere);
    let extras = getValues("booking_payments")?.filter(
      (item) => item.connected_resource.type == "extra"
    );
    //console.log("Extras", extras);
    let total_penalty = 0;
    let rows = getValues("booking_items")?.map((item) => {
      let payment = camere.find(
        (camera) => camera.connected_resource.content.id == item.id
      );
      //console.log(payment);
      total_penalty += payment.connected_resource.penalty;
      return {
        id: item.id,
        accommodation: item.accommodation?.name,
        quantity: item.quantity,
        ratePlan: item.rate_plan?.name,
        totGuests: item.prezziario?.tot_guests,
        children: item.children,
        treatment: item.treatment?.name,
        price: item.price,
        penalty: payment.connected_resource.penalty,
        status: payment.status,
      };
    });
    setTotalPenalty(total_penalty);
    if (getValues("booking_items")?.length > 0) setRowsSistemazioni(rows);

    //extras
    rows = [];
    if (extras && extras.length > 0) {
      rows = extras.map((item) => {
        return {
          id: item.id,
          extra: item.connected_resource.content.name,
          quantity: item.connected_resource.content.quantity,
          price_type: item.connected_resource.content.price_type.name,
          price: item.connected_resource.price,
          status: item.status,
        };
      });
      setRowsExtras(rows);
    }
  }, [getValues("booking_items"), getValues("booking_payments")]);

  return (
    <div>
      <Grid container spacing={2}>
        {fields.map((field, index) => {
          return (
            <AutoGeneratedInputCmp
              key={index}
              disabled={disabled}
              defaultValue={watch(field.name)}
              {...field}
              register={register}
              control={control}
              errors={errors}
              setError={setError}
              fieldVariants="outlined"
              defaultLanguageRecord={defaultLanguageRecord}
              getValues={getValues}
              setValue={setValue}
              watch={watch}
            />
          );
        })}
      </Grid>

      <Grid container spacing={2} mt={1}>
        <Grid item xs={12}>
          <DataGrid
            rows={rowsSistemazioni}
            columns={columnsSistemazioni}
            pageSize={rowsSistemazioni.length}
            pagination
            components={{
              Pagination: () => {
                return null;
              },
            }}
            autoHeight
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                background: "rgb(242, 242, 242)",
                borderBottom: "1px solid rgb(229, 229, 229)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgb(242, 242, 242)",
              },
            }}
            disableSelectionOnClick={true}
          />
        </Grid>

        {
          /* RIEPILOGO EXTRA */
          rowsExtras.length > 0 && (
            <Grid item xs={12}>
              <DataGrid
                rows={rowsExtras}
                columns={columnsExtras}
                pageSize={rowsExtras.length}
                pagination
                components={{
                  Pagination: () => {
                    return null;
                  },
                }}
                autoHeight
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    background: "rgb(242, 242, 242)",
                    borderBottom: "1px solid rgb(229, 229, 229)",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid rgb(242, 242, 242)",
                  },
                }}
                disableSelectionOnClick={true}
              />
            </Grid>
          )
        }
      </Grid>
      <Grid container mt={3}>
        <Grid
          item
          xs={12}
          md={4}
          container
          justifyContent="flex-start"
          flexDirection="column"
        >
          <Typography variant="body1" gutterBottom>
            Totale:{" "}
            <span style={{ fontWeight: 700 }}>{getValues("price")} €</span>
          </Typography>
          <Typography variant="body1">
            Totale penale:{" "}
            <span style={{ fontWeight: 700 }}>{totalPenalty} €</span>
          </Typography>
        </Grid>
        <Grid item xs={12} md={8} container justifyContent="flex-end" gap={3}>
          <LoadingButton
            variant="contained"
            color="success"
            size="small"
            sx={{ minWidth: 150 }}
            onClick={() => {
              setConfirmationType("full");
              handleOpen();
            }}
            loading={requestingPayment}
            disabled={!canRequestPayment}
          >
            Richiedi pagamento
          </LoadingButton>

          <LoadingButton
            variant="contained"
            color="error"
            size="small"
            sx={{ minWidth: 150 }}
            onClick={() => {
              setConfirmationType("no-show");
              handleOpen();
            }}
            loading={requestingPayment}
            disabled={!canRequestPayment}
          >
            NO SHOW
          </LoadingButton>

          <LoadingButton
            variant="contained"
            color="customBlue"
            size="small"
            sx={{ minWidth: 150 }}
            onClick={async () => {
              toast.error(
                "Funzionalità di notifica pagamento in loco da implementare"
              );
            }}
            loading={requestingPayment}
            disabled={!canRequestPayment}
          >
            PAGATO
          </LoadingButton>
        </Grid>
      </Grid>

      <ConfirmationModal
        open={openConfirmation}
        handleClose={handleClose}
        handleConfirmation={
          confirmationType == "full" ? fullPaymentRequest : noShowRequest
        }
        confirmationType={confirmationType}
        data={
          confirmationType == "full"
            ? getValues("price")
            : getValues("booking_payments")?.reduce(
                (accumulator, item) =>
                  Number(accumulator) +
                  Number(item.connected_resource?.penalty || 0),
                0
              )
        } //evaluate total penalty
      />
    </div>
  );
}
